import { getCaptecPerformanceProfile } from "./scrollPerformance";

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const segment = (progress, start, end) =>
  clamp01((progress - start) / Math.max(0.0001, end - start));
const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
const easeInOutCubic = (value) =>
  value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;

const createNode = (tag, className, parent) => {
  const node = document.createElement(tag);
  node.className = className;
  parent?.appendChild(node);
  return node;
};

const setStyles = (node, styles) => {
  if (!node) return;
  Object.entries(styles).forEach(([property, value]) => {
    node.style[property] = value;
  });
};

const removeNode = (node) => {
  if (!node) return null;
  node.remove();
  return null;
};

const CARD_LAYOUTS = [
  { x: -26, y: -16, z: 72, rx: -7, ry: 12, rz: -12 },
  { x: 24, y: -14, z: 18, rx: 5, ry: -14, rz: 11 },
  { x: -21, y: 18, z: -14, rx: 7, ry: 11, rz: 7 },
  { x: 21, y: 16, z: 80, rx: -6, ry: -9, rz: -10 },
];

const CHIP_LAYOUTS = [
  { x: -9, y: 4, z: 112, rz: -16 },
  { x: 12, y: 7, z: 76, rz: 20 },
];

const createCard = (root, index) => {
  const card = createNode(
    "div",
    `immersive-dom-card immersive-dom-card--${index + 1}`,
    root
  );
  createNode("span", "immersive-dom-card__chip", card);
  createNode("span", "immersive-dom-card__line", card);
  return card;
};

const createChip = (root, index) => {
  const chip = createNode(
    "div",
    `immersive-dom-chip immersive-dom-chip--${index + 1}`,
    root
  );
  createNode("span", "immersive-dom-chip__line immersive-dom-chip__line--a", chip);
  createNode("span", "immersive-dom-chip__line immersive-dom-chip__line--b", chip);
  createNode("span", "immersive-dom-chip__line immersive-dom-chip__line--c", chip);
  return chip;
};

const createParticles = (root, count) => {
  const particles = createNode("div", "immersive-dom-particles", root);

  for (let index = 0; index < count; index += 1) {
    const particle = createNode("span", "immersive-dom-particle", particles);
    const angle = (index / count) * Math.PI * 2;
    const ring = index % 3;
    const radiusX = 22 + ring * 9;
    const radiusY = 17 + ring * 6;
    const x = Math.cos(angle) * radiusX;
    const y = Math.sin(angle) * radiusY;
    const size = 2 + (index % 2);

    setStyles(particle, {
      left: `${50 + x}%`,
      top: `${50 + y}%`,
      width: `${size}px`,
      height: `${size}px`,
      opacity: `${0.34 + (index % 3) * 0.14}`,
    });
  }

  return particles;
};

export const createPaymentEcosystemScene = (host) => {
  if (!host) throw new Error("Scene host is required");

  const { lowResource } = getCaptecPerformanceProfile();
  const compact = lowResource || window.matchMedia("(max-width: 900px)").matches;
  const root = createNode("div", "immersive-dom-scene", host);
  let cards = [];
  let chips = [];
  let particles = null;
  let disposed = false;
  let activeProgress = 0;
  let pointerX = 0;
  let pointerY = 0;

  const ensureCardsAndChips = () => {
    if (!cards.length) {
      const cardCount = lowResource ? 2 : compact ? 3 : 4;
      cards = Array.from({ length: cardCount }, (_, index) =>
        createCard(root, index)
      );
    }

    if (!chips.length) {
      const chipCount = lowResource || compact ? 1 : 2;
      chips = Array.from({ length: chipCount }, (_, index) =>
        createChip(root, index)
      );
    }
  };

  const ensureParticles = () => {
    if (!particles) {
      particles = createParticles(root, lowResource ? 6 : compact ? 10 : 16);
    }
  };

  const removeCardsAndChips = () => {
    cards.forEach((card) => card.remove());
    chips.forEach((chip) => chip.remove());
    cards = [];
    chips = [];
  };

  const removeParticles = () => {
    particles = removeNode(particles);
  };

  const updateCardsAndChips = (progress) => {
    if (progress >= 0.16 && progress < 0.8) ensureCardsAndChips();

    cards.forEach((card, index) => {
      const layout = CARD_LAYOUTS[index] || CARD_LAYOUTS[0];
      const emerge = easeOutCubic(
        segment(progress, 0.18 + index * 0.018, 0.4 + index * 0.012)
      );
      const drift = easeInOutCubic(
        segment(progress, 0.42 + index * 0.01, 0.58 + index * 0.012)
      );
      const exit = easeInOutCubic(
        segment(progress, 0.58 + index * 0.01, 0.75 + index * 0.01)
      );
      const x = layout.x * emerge + layout.x * 0.18 * drift + layout.x * 0.5 * exit;
      const y = layout.y * emerge + Math.sin(index + progress * 5) * 2 * drift + layout.y * 0.4 * exit;
      const z = layout.z * emerge + 26 * drift + 110 * exit;
      const scale = Math.max(0.001, (0.2 + emerge * 0.8) * (1 - exit * 0.42));

      setStyles(card, {
        opacity: `${Math.max(0, emerge * (1 - exit))}`,
        transform: `translate3d(calc(-50% + ${x.toFixed(2)}vw), calc(-50% + ${y.toFixed(
          2
        )}vh), ${z.toFixed(1)}px) rotateX(${(
          layout.rx * emerge
        ).toFixed(2)}deg) rotateY(${(layout.ry * emerge).toFixed(
          2
        )}deg) rotateZ(${(layout.rz * emerge + drift * 3).toFixed(2)}deg) scale(${scale.toFixed(
          4
        )})`,
      });
    });

    chips.forEach((chip, index) => {
      const layout = CHIP_LAYOUTS[index] || CHIP_LAYOUTS[0];
      const emerge = easeOutCubic(
        segment(progress, 0.22 + index * 0.02, 0.45 + index * 0.016)
      );
      const drift = easeInOutCubic(
        segment(progress, 0.44 + index * 0.01, 0.57 + index * 0.01)
      );
      const exit = easeInOutCubic(
        segment(progress, 0.58 + index * 0.01, 0.74 + index * 0.012)
      );
      const x = layout.x * emerge + layout.x * 0.18 * drift + layout.x * 0.72 * exit;
      const y = layout.y * emerge + (index === 0 ? -3 : 4) * drift + 10 * exit;
      const z = layout.z * emerge + 18 * drift + 104 * exit;
      const rotation = layout.rz * emerge + drift * 10 + 60 * exit;
      const scale = Math.max(0.001, (0.18 + emerge * 0.82) * (1 - exit * 0.5));

      setStyles(chip, {
        opacity: `${Math.max(0, emerge * (1 - exit))}`,
        transform: `translate3d(calc(-50% + ${x.toFixed(2)}vw), calc(-50% + ${y.toFixed(
          2
        )}vh), ${z.toFixed(1)}px) rotateZ(${rotation.toFixed(2)}deg) scale(${scale.toFixed(
          4
        )})`,
      });
    });
  };

  const updateParticles = (progress) => {
    if (progress >= 0.24 && progress < 0.72) ensureParticles();
    if (!particles) return;

    const enter = easeOutCubic(segment(progress, 0.24, 0.4));
    const drift = easeInOutCubic(segment(progress, 0.4, 0.58));
    const exit = easeInOutCubic(segment(progress, 0.58, 0.72));

    setStyles(particles, {
      opacity: `${enter * (1 - exit)}`,
      transform: `translate3d(-50%, -50%, 0) rotate(${(
        progress * 14 + drift * 4
      ).toFixed(2)}deg) scale(${(0.86 + enter * 0.12 + drift * 0.05).toFixed(
        4
      )})`,
    });
  };

  const update = (progress) => {
    if (disposed) return;
    activeProgress = clamp01(progress);

    updateCardsAndChips(activeProgress);
    updateParticles(activeProgress);

    root.style.transform = `translate3d(${(pointerX * (lowResource ? 0 : 5)).toFixed(2)}px, ${(
      pointerY * (lowResource ? 0 : 3)
    ).toFixed(2)}px, 0)`;

    if (activeProgress >= 0.72) removeParticles();
    if (activeProgress >= 0.8) removeCardsAndChips();
  };

  const resize = () => {
    // Viewport-relative layout, nothing expensive required.
  };

  const setPointer = (x, y) => {
    if (disposed) return;
    if (lowResource) return;
    pointerX = Math.max(-1, Math.min(1, x));
    pointerY = Math.max(-1, Math.min(1, y));
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    removeParticles();
    removeCardsAndChips();
    root.remove();
  };

  update(0);

  return {
    update,
    resize,
    setPointer,
    dispose,
    get progress() {
      return activeProgress;
    },
  };
};

export default createPaymentEcosystemScene;
